import { Action, Dispatch, Reducer } from "redux";
import { CacheRequestState, EmptyRequestState, RequestState } from "../models/RequestState";
import StatusCode from "../util/StatusCode";
import { CoreAppToken, CoreAppTokenValidation } from "../models/CoreAppToken";
import InstanceClient from "../services/InstanceClient";
import { AppTokenType } from "../models/CoreModels";


export enum PasswordAppTokenType {
    LIST_APPTOKEN = 'LIST_APPTOKEN',
    LIST_APPTOKEN_UPDATE = 'LIST_APPTOKEN_UPDATE',
    CREATE_APPTOKEN = 'CREATE_APPTOKEN',
    VALIDATE_APPTOKEN = 'VALIDATE_APPTOKEN',
    DELETE_APPTOKEN = 'DELETE_APPTOKEN',
}

export interface PasswordAppTokenState {
    apptoken: CacheRequestState<CoreAppToken[]>
    createAppToken: RequestState<CoreAppToken>
    appTokenValidation: RequestState<{ id: string, valid: boolean }[]>
    deleteAppToken: EmptyRequestState
}

interface ListPasswordAppToken extends Action<PasswordAppTokenType.LIST_APPTOKEN> {
    apptoken: CacheRequestState<CoreAppToken[]>
}

interface CreatePasswordAppToken extends Action<PasswordAppTokenType.CREATE_APPTOKEN> {
    createAppToken: RequestState<CoreAppToken>
}

interface ValidatePasswordAppToken extends Action<PasswordAppTokenType.VALIDATE_APPTOKEN> {
    appTokenValidation: RequestState<{ id: string, valid: boolean }[]>
}
interface DeletePasswordAppToken extends Action<PasswordAppTokenType.DELETE_APPTOKEN> {
    deleteAppToken: EmptyRequestState
}

interface ListPasswordAppTokensUpdate extends Action<PasswordAppTokenType.LIST_APPTOKEN_UPDATE> { }

type PasswordAppTokenAction = ListPasswordAppToken
    | ListPasswordAppTokensUpdate
    | CreatePasswordAppToken
    | ValidatePasswordAppToken
    | DeletePasswordAppToken;


export const ListPasswordAppTokens = (update: boolean = false) => (dispatch: Dispatch<PasswordAppTokenAction>) => {
    if (update) {
        dispatch({
            type: PasswordAppTokenType.LIST_APPTOKEN_UPDATE,
        })
    } else {
        dispatch({
            type: PasswordAppTokenType.LIST_APPTOKEN,
            apptoken: { code: StatusCode.PENDING }
        })
    }
    InstanceClient.get<CoreAppToken[]>('/api/core/passwordapptoken')
        .then(res =>
            dispatch({
                type: PasswordAppTokenType.LIST_APPTOKEN,
                apptoken: { code: StatusCode.COMPLETE, data: res.data }
            }))
        .catch(error =>
            dispatch({
                type: PasswordAppTokenType.LIST_APPTOKEN,
                apptoken: { code: StatusCode.ERROR, error }
            }));
}


export const ValidateAppTokens = (appTokenIds: string[]) => async (dispatch: Dispatch<PasswordAppTokenAction>) => {
    dispatch({
        type: PasswordAppTokenType.VALIDATE_APPTOKEN,
        appTokenValidation: { code: StatusCode.PENDING }
    });

    try {
        const results = await Promise.allSettled(
            appTokenIds.map(id =>
                InstanceClient.get<CoreAppTokenValidation>(
                    `/api/core/apptoken/${id}/validate`,
                    { additionalScopes: [] }
                )
            )
        );

        const mappedResults = results.map((result, index) => {
            const id = appTokenIds[index];
            if (result.status === 'fulfilled') {
                return {
                    id,
                    valid: result.value.data.valid,
                };
            }
            return {
                id,
                valid: false,
            };
        });

        dispatch({
            type: PasswordAppTokenType.VALIDATE_APPTOKEN,
            appTokenValidation: {
                code: StatusCode.COMPLETE,
                data: mappedResults
            }
        });
    } catch (error: any) {
        dispatch({
            type: PasswordAppTokenType.VALIDATE_APPTOKEN,
            appTokenValidation: { code: StatusCode.ERROR, error }
        });

    }
}

export const ClearValidateAppTokens = () => (dispatch: Dispatch<PasswordAppTokenAction>) => {
    dispatch({
        type: PasswordAppTokenType.VALIDATE_APPTOKEN,
        appTokenValidation: { code: StatusCode.NONE }
    });
}

export const CreateAppToken = (type: AppTokenType) => (dispatch: Dispatch<PasswordAppTokenAction>) => {
    const body = {
        type: type
    }
    dispatch({
        type: PasswordAppTokenType.CREATE_APPTOKEN,
        createAppToken: { code: StatusCode.PENDING }
    })
    InstanceClient.post<CoreAppToken, { type: AppTokenType }>('/api/core/passwordapptoken', body)
        .then(res =>
            dispatch({
                type: PasswordAppTokenType.CREATE_APPTOKEN,
                createAppToken: { code: StatusCode.COMPLETE, data: res.data }
            }))
        .catch(error =>
            dispatch({
                type: PasswordAppTokenType.CREATE_APPTOKEN,
                createAppToken: { code: StatusCode.ERROR, error }
            }));
}

export const ClearCreateAppToken = () => (dispatch: Dispatch<PasswordAppTokenAction>) => {
    dispatch({
        type: PasswordAppTokenType.CREATE_APPTOKEN,
        createAppToken: { code: StatusCode.NONE }
    })
}

export const DeleteAppToken = (appTokenId: string) => (dispatch: Dispatch<PasswordAppTokenAction>) => {
    dispatch({
        type: PasswordAppTokenType.DELETE_APPTOKEN,
        deleteAppToken: { code: StatusCode.PENDING }
    })
    InstanceClient.delete(`/api/core/passwordapptoken/${appTokenId}`)
        .then(res =>
            dispatch({
                type: PasswordAppTokenType.DELETE_APPTOKEN,
                deleteAppToken: { code: StatusCode.COMPLETE }
            }))
        .catch(error =>
            dispatch({
                type: PasswordAppTokenType.DELETE_APPTOKEN,
                deleteAppToken: { code: StatusCode.ERROR, error }
            }))
}

export const ClearDeleteAppToken = () => (dispatch: Dispatch<PasswordAppTokenAction>) => {
    dispatch({
        type: PasswordAppTokenType.DELETE_APPTOKEN,
        deleteAppToken: { code: StatusCode.NONE }
    })
}

const initialState: PasswordAppTokenState = {
    apptoken: { code: StatusCode.NONE },
    createAppToken: { code: StatusCode.NONE },
    appTokenValidation: { code: StatusCode.NONE },
    deleteAppToken: { code: StatusCode.NONE },
}

export const reducer: Reducer<PasswordAppTokenState, PasswordAppTokenAction> = (state, action) => {
    state = state || initialState;

    switch (action.type) {
        case PasswordAppTokenType.LIST_APPTOKEN:
            return {
                ...state,
                apptoken: action.apptoken
            };
        case PasswordAppTokenType.LIST_APPTOKEN_UPDATE:
            return {
                ...state,
                apptoken: { ...state.apptoken, code: StatusCode.PENDING }
            };
        case PasswordAppTokenType.CREATE_APPTOKEN:
            return {
                ...state,
                createAppToken: action.createAppToken
            };
        case PasswordAppTokenType.VALIDATE_APPTOKEN:
            return {
                ...state,
                appTokenValidation: action.appTokenValidation
            };
        case PasswordAppTokenType.DELETE_APPTOKEN:
            return {
                ...state,
                deleteAppToken: action.deleteAppToken
            };
        default:
            return state;
    }
}